import React, { useState, useEffect } from 'react';
import {
  CONSENT_STORAGE_KEY,
  DEFAULT_CONSENT,
  applyConsentToGtag,
  getStoredConsent,
} from '../../utils/cookieConsent';
import { initGA } from '../../utils/analytics';
import './CookieConsent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const savedConsent = getStoredConsent();
    if (!savedConsent) {
      // Opt-out model: no stored decision means analytics is granted by
      // default, so initialise GA while still prompting via the banner.
      setShowBanner(true);
      applyConsentToGtag(DEFAULT_CONSENT);
      if (DEFAULT_CONSENT.analytics) {
        initGA();
      }
      return;
    }
    setConsent(savedConsent);
    applyConsentToGtag(savedConsent);
    if (savedConsent.analytics) {
      initGA();
    }
  }, []);

  const initializeServices = (consentData) => {
    applyConsentToGtag(consentData);
    if (consentData.analytics) {
      initGA();
    }
  };

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setConsent(allConsent);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(allConsent));
    setShowBanner(false);
    initializeServices(allConsent);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    setShowBanner(false);
    initializeServices(consent);
  };

  const handleRejectAll = () => {
    const minimalConsent = { ...DEFAULT_CONSENT };
    setConsent(minimalConsent);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(minimalConsent));
    setShowBanner(false);
    initializeServices(minimalConsent);
  };

  const handleConsentChange = (type) => {
    if (type === 'necessary') return;

    setConsent((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <div className="cookie-consent-header">
            <h3>🍪 Cookie-samtykke</h3>
            {/*
              Intentional: "×" dismisses the banner for this session only.
              No consent is persisted — the banner re-appears on next visit,
              and analytics_storage defaults to 'granted' until explicitly denied.
            */}
            <button
              className="cookie-consent-close"
              onClick={() => setShowBanner(false)}
              aria-label="Luk cookie-banner"
            >
              ×
            </button>
          </div>

          <div className="cookie-consent-body">
            <p>
              Vi bruger cookies til at forbedre din oplevelse på vores website.
              Nogle cookies er nødvendige for at hjemmesiden fungerer korrekt,
              mens andre hjælper os med at forstå, hvordan du bruger vores website.
            </p>

            <div className="cookie-consent-actions">
              <button
                className="cookie-consent-btn cookie-consent-btn-primary"
                onClick={handleAcceptAll}
              >
                Accepter alle
              </button>
              <button
                className="cookie-consent-btn cookie-consent-btn-secondary"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Skjul detaljer' : 'Vælg cookies'}
              </button>
              <button
                className="cookie-consent-btn cookie-consent-btn-outline"
                onClick={handleRejectAll}
              >
                Afvis alle
              </button>
            </div>

            {showDetails && (
              <div className="cookie-consent-details">
                <h4>Cookie-kategorier</h4>

                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-category-label">
                      <input
                        type="checkbox"
                        checked={consent.necessary}
                        disabled
                        className="cookie-category-checkbox"
                      />
                      <span className="cookie-category-title">Nødvendige cookies</span>
                    </label>
                  </div>
                  <p className="cookie-category-description">
                    Disse cookies er nødvendige for at hjemmesiden fungerer korrekt.
                    De kan ikke deaktiveres.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-category-label">
                      <input
                        type="checkbox"
                        checked={consent.functional}
                        onChange={() => handleConsentChange('functional')}
                        className="cookie-category-checkbox"
                      />
                      <span className="cookie-category-title">Funktionelle cookies</span>
                    </label>
                  </div>
                  <p className="cookie-category-description">
                    Disse cookies husker dine præferencer og indstillinger for at
                    give dig en bedre oplevelse.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-category-label">
                      <input
                        type="checkbox"
                        checked={consent.analytics}
                        onChange={() => handleConsentChange('analytics')}
                        className="cookie-category-checkbox"
                      />
                      <span className="cookie-category-title">Analytiske cookies</span>
                    </label>
                  </div>
                  <p className="cookie-category-description">
                    Disse cookies hjælper os med at forstå, hvordan du bruger vores
                    website, så vi kan forbedre den.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-category-label">
                      <input
                        type="checkbox"
                        checked={consent.marketing}
                        onChange={() => handleConsentChange('marketing')}
                        className="cookie-category-checkbox"
                      />
                      <span className="cookie-category-title">Marketing cookies</span>
                    </label>
                  </div>
                  <p className="cookie-category-description">
                    Disse cookies bruges til at vise dig relevante annoncer og
                    måle effektiviteten af vores marketingkampagner.
                  </p>
                </div>

                <div className="cookie-consent-detail-actions">
                  <button
                    className="cookie-consent-btn cookie-consent-btn-primary"
                    onClick={handleAcceptSelected}
                  >
                    Gem valg
                  </button>
                </div>
              </div>
            )}

            <div className="cookie-consent-footer">
              <p>
                Læs mere om vores brug af cookies i vores{' '}
                <a href="/privatlivspolitik" className="cookie-consent-link">
                  privatlivspolitik
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
